import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Box, Grid, Tooltip } from '@mui/material';
import {
  Feature,
  ReadWriteFeatureMappingData,
  SimpleFeatureMappingData,
} from '@loyalty/shared-common';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { Link } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { QlfNavigationTab, colorPalette } from '@qualifio/ui-commons';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { makeStyles } from '@mui/styles';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExportProfiles } from './ExportProfiles';
import { GoToProgramButton } from './GoToProgramButton';
import { Router } from '../../services';
import {
  burningRulesIcon,
  earningRulesIcon,
  editorIcon,
  profilesIcons,
  reportIcon,
  rewardsIcon,
} from '../icons';
import { useFeatures } from '../../features/FeaturesContext';
import { useProgram } from '../../loyalty/ProgramContext';

const useStyle = makeStyles(() => ({
  bar: {
    backgroundColor: colorPalette.grey.light,
    height: '65px',
    paddingLeft: '20px',
    paddingRight: '20px',
    paddingTop: '15px',
  },
  link: {
    textDecoration: 'none !important',
    '& div': {
      padding: '15px',
    },
  },
  linkLabel: {
    '@media (max-width: 1065px)': {
      '&': {
        display: 'none',
      },
    },
  },
  reportIcon: {
    marginTop: 8,
    transform: 'translateX(2px)',
  },
  disabled: {
    pointerEvents: 'none',
  },
}));

export function LoyaltyTopbar(): React.ReactElement {
  const classes = useStyle();
  const location = useLocation();
  const { id, accountId } = useParams<{ id: string; accountId: string }>();
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { program } = useProgram();

  const { enabled: canExportProfiles } = features[Feature.EXPORT_PROFILES];

  const isPathnameActive = useCallback(
    (name: string): boolean => {
      const path = Router.getPath(name, { id, accountId });
      return !!path && location.pathname.startsWith(path);
    },
    [location, id, accountId],
  );

  const links: { label: string; name: string; icon: IconDefinition; feature: Feature }[] = [
    {
      label: t('loyalty-topbar.actions.report'),
      name: 'report',
      icon: reportIcon,
      feature: Feature.REPORT,
    },
    {
      label: t('loyalty-topbar.actions.profiles'),
      name: 'profiles',
      icon: profilesIcons,
      feature: Feature.PARTICIPANT,
    },
    {
      label: t('topbar.tabs.earning-rules'),
      name: 'earningRules',
      icon: earningRulesIcon,
      feature: Feature.EARNING_RULE,
    },
    {
      label: t('topbar.tabs.rewards'),
      name: 'rewards',
      icon: rewardsIcon,
      feature: Feature.REWARD,
    },
    {
      label: t('topbar.tabs.burning-rules'),
      name: 'burningRules',
      icon: burningRulesIcon,
      feature: Feature.BURNING_RULE,
    },
    { label: t('topbar.tabs.editor'), name: 'editor', icon: editorIcon, feature: Feature.TEMPLATE },
    {
      label: t('topbar.tabs.program-settings'),
      name: 'settings',
      icon: faCog,
      feature: Feature.PROGRAM,
    },
  ];

  return (
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      className={classes.bar}
    >
      <Box sx={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center' }}>
        {links.map((link, index) => {
          const enabled: boolean =
            (features[link.feature] as ReadWriteFeatureMappingData<boolean>).read ||
            (features[link.feature] as SimpleFeatureMappingData<boolean>).enabled;
          return (
            <Tooltip
              key={link.name}
              title={enabled ? '' : t('shared.action.permission.required')}
              placement="bottom"
            >
              <div>
                <Link
                  to={Router.getPath(link.name, { id, accountId })}
                  className={classNames(classes.link, !enabled ? classes.disabled : undefined)}
                >
                  <QlfNavigationTab
                    disabled={!enabled}
                    key={link.name}
                    active={isPathnameActive(link.name)}
                    icon={link.icon}
                    variant={'shell'}
                    shellLeftBorderRadius={index !== 0}
                  >
                    <span className={classes.linkLabel}>{link.label}</span>
                  </QlfNavigationTab>
                </Link>
              </div>
            </Tooltip>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyItems: 'space-between',
        }}
      >
        {isPathnameActive('profiles') && canExportProfiles && (
          <>
            <ExportProfiles />
            <Box
              sx={{
                background: colorPalette.grey.main,
                marginRight: 1,
                marginLeft: 1,
                height: '25px',
                width: '2px',
              }}
              component="span"
            />
          </>
        )}
        <GoToProgramButton programId={program.id} />
      </Box>
    </Grid>
  );
}
